/**
 * CompanyBadge — A reusable company/brand icon component for Remotion templates.
 *
 * Renders the brand's SVG icon from simple-icons data, with a styled container
 * that adapts to each template's aesthetic. Falls back to a monogram if no icon found.
 *
 * Style presets:
 *  - "minimal"  → Bare icon, no background (ZenMinimal, EditorialCream, LuxuryGold)
 *  - "glass"    → Frosted glass circle (MinimalGlass, EtherealPrism, AuroraNature)
 *  - "glow"     → Neon glow ring (CyberHUD, Cyberpunk2099, RetroTerminal)
 *  - "badge"    → Solid rounded-rect (BoldCreator, SportsEnergy, CorporatePro, Urban)
 */
import React from 'react';
import { brandIconMap } from './brandIcons';
import { getMonogram } from './companyDetector';

export type CompanyBadgeStyle = 'minimal' | 'glass' | 'glow' | 'badge';

export interface CompanyBadgeProps {
  /** Brand slug (key in brandIconMap) or raw company name for monogram fallback */
  name: string;
  /** Tint color for the icon (hex with #). Defaults to white. */
  color?: string;
  /** Background/glow color (hex with #). Used by glass, glow, badge presets. */
  accentColor?: string;
  /** Size of the entire badge container in pixels */
  size?: number;
  /** Visual style preset */
  style?: CompanyBadgeStyle;
  /** Opacity of the entire badge (0–1) */
  opacity?: number;
  /** Additional container style overrides */
  containerStyle?: React.CSSProperties;
  /** Animation transform string applied to outer container */
  transform?: string;
}

/**
 * Returns the SVG icon path + title for a slug, or null if not found.
 */
function getBrandIcon(name: string) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return brandIconMap[slug] ?? brandIconMap[name] ?? null;
}

export const CompanyBadge: React.FC<CompanyBadgeProps> = ({
  name,
  color = '#ffffff',
  accentColor = '#ffffff',
  size = 80,
  style = 'glass',
  opacity = 1,
  containerStyle = {},
  transform,
}) => {
  const icon = getBrandIcon(name);
  const iconSize = size * 0.55;
  const monogram = getMonogram(name);

  // ── Shared SVG renderer ────────────────────────────────────────────────────
  const renderIcon = () => {
    if (!icon) {
      // Monogram fallback
      return (
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 800,
            fontSize: iconSize * 0.65,
            color,
            lineHeight: 1,
            letterSpacing: '-1px',
            userSelect: 'none',
          }}
        >
          {monogram}
        </span>
      );
    }

    return (
      <svg
        viewBox="0 0 24 24"
        width={iconSize}
        height={iconSize}
        fill={color}
        style={{ display: 'block', flexShrink: 0 }}
        aria-label={icon.title}
      >
        <path d={icon.path} />
      </svg>
    );
  };

  // ── Style: minimal ─────────────────────────────────────────────────────────
  if (style === 'minimal') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          opacity,
          transform,
          ...containerStyle,
        }}
      >
        {renderIcon()}
      </div>
    );
  }

  // ── Style: glass ───────────────────────────────────────────────────────────
  if (style === 'glass') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: `1.5px solid ${accentColor}30`,
          boxShadow: `0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)`,
          opacity,
          transform,
          ...containerStyle,
        }}
      >
        {renderIcon()}
      </div>
    );
  }

  // ── Style: glow ────────────────────────────────────────────────────────────
  if (style === 'glow') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          borderRadius: '50%',
          background: `${accentColor}14`,
          border: `1.5px solid ${accentColor}`,
          boxShadow: `0 0 ${size * 0.4}px ${accentColor}55, 0 0 ${size * 0.15}px ${accentColor}88, inset 0 0 ${size * 0.3}px ${accentColor}22`,
          opacity,
          transform,
          ...containerStyle,
        }}
      >
        {renderIcon()}
      </div>
    );
  }

  // ── Style: badge ───────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: size * 0.22,
        background: accentColor,
        boxShadow: `0 4px 16px ${accentColor}55`,
        opacity,
        transform,
        ...containerStyle,
      }}
    >
      {/* Icon rendered dark on solid color background */}
      <svg
        viewBox="0 0 24 24"
        width={iconSize}
        height={iconSize}
        fill={icon ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.7)'}
        style={{ display: 'block', flexShrink: 0 }}
        aria-label={icon?.title ?? name}
      >
        {icon ? (
          <path d={icon.path} />
        ) : (
          <text
            x="12"
            y="16"
            textAnchor="middle"
            fontSize="10"
            fontWeight="800"
            fontFamily="Inter, sans-serif"
            fill="rgba(0,0,0,0.85)"
          >
            {monogram}
          </text>
        )}
      </svg>
    </div>
  );
};

/**
 * Inline brand label — shows a small icon + brand name side by side.
 * Useful for inline mentions within bullet text.
 */
export const BrandLabel: React.FC<{
  name: string;
  color?: string;
  accentColor?: string;
  fontSize?: number;
  badgeStyle?: CompanyBadgeStyle;
}> = ({ name, color = '#ffffff', accentColor = '#ffffff', fontSize = 14, badgeStyle = 'glow' }) => {
  const icon = getBrandIcon(name);
  const iconSize = fontSize * 1.4;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: fontSize * 0.4,
        verticalAlign: 'middle',
      }}
    >
      <CompanyBadge
        name={name}
        color={color}
        accentColor={accentColor}
        size={iconSize * 1.8}
        style={badgeStyle}
      />
      <span
        style={{
          fontWeight: 700,
          fontSize,
          color,
          fontFamily: 'inherit',
        }}
      >
        {icon?.title ?? name}
      </span>
    </span>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
